import {
  Box,
  Button,
  Card,
  CardHeader,
  Container,
  Divider,
  Grid,
  Skeleton,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { Outlet, useParams } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import TabPanel from '../../../../components/molecules/tab-panel';
import { ArrowLeft as ArrowLeftIcon } from '../../../../icons/arrow-left';
import { ExclamationOutlined as ExclamationOutlinedIcon } from '../../../../icons/exclamation-outlined';
import { useDiscountTemplates } from '../../hooks/query/use-discount-templates';
import { useHost } from '../../hooks/query/use-host';
import { useHostDiscountHistory } from '../../hooks/query/use-host-discount-history';
import { BillingGroupInfo } from '../molecules/host/billing-group-info';
import { DestinationGroupInfo } from '../molecules/host/destination-group-info';
import { DestinationInfo } from '../molecules/host/destination-info';
import { DiscountInfo } from '../molecules/host/discount-info';
import { HostInfo } from '../molecules/host/host-info';
import { UpdateHostBillingGroupDialog } from '../organisms/dialogs/update-host-billing-group-dialog';
import { UpdateHostDestinationGroupDialog } from '../organisms/dialogs/update-host-destination-group-dialog';
import { UpdateHostDestinationsDialog } from '../organisms/dialogs/update-host-destinations-dialog';
import { UpdateHostDiscountsDialog } from '../organisms/dialogs/update-host-discounts-dialog';
import { HostsDiscountHistory } from '../organisms/hosts-discount-history';

import { Pagination } from '@/components/organisms/pagination';
import { useDialog } from '@/hooks/use-dialog';
import { useNavigateBack } from '@/hooks/use-navigate-back';
import { usePagination } from '@/hooks/use-pagination';
import { withPermissions } from '@/permissions/can';

const Host = () => {
  const { t } = useTranslation(['common', 'host']);
  const { navigateBack } = useNavigateBack();

  const hostId = useParams({
    from: '/_layout/hosts/$hostId',
    select: state => (state.hostId ? +state.hostId : 0),
  });

  const [activeTab, setActiveTab] = useState(0);

  const tabs = [
    {
      label: t('common:information'),
    },
    {
      label: t('host:discount.discount'),
    },
  ];

  const { changePage, changePerPage, page, perPage, setTotalItems, totalPages } = usePagination();

  const {
    data: discountHistoryData,
    isLoading: isLoadingDiscountHistory,
    isFetching: isFetchingDiscountHistory,
    error: discountHistoryError,
  } = useHostDiscountHistory({ hostId, page, perPage });

  const { data, isLoading, error } = useHost(hostId);

  const { data: discountsTemplatesData, isLoading: isLoadingDiscountsTemplates } =
    useDiscountTemplates();

  const [
    isOpenUpdateDiscountDialog,
    handleOpenUpdateDiscountDialog,
    handleCloseUpdateDiscountDialog,
  ] = useDialog();

  const [
    isOpenUpdateHostDestinationsDialog,
    handleOpenHostDestinationsDialog,
    handleCloseUpdateHostDestinationsDialog,
  ] = useDialog();

  //Billing groups
  const [
    isOpenUpdateBillingGroupDialog,
    handleOpenGroupDialog,
    handleCloseUpdateBillingGroupDialog,
  ] = useDialog();

  //Destination groups

  const [
    isOpenUpdateDestinationGroupDialog,
    handleOpenDestinationGroupDialog,
    handleCloseUpdateDestinationGroupDialog,
  ] = useDialog();

  useEffect(() => {
    if (discountHistoryData) {
      setTotalItems(discountHistoryData?.total);
    }
  }, [discountHistoryData]);

  const renderContent = () => {
    if (isLoading || isLoadingDiscountsTemplates) {
      return (
        <Box sx={{ py: 4 }}>
          <Skeleton height={42} />
          <Skeleton />
          <Skeleton />
        </Box>
      );
    }

    if (!data || error) {
      return (
        <Box sx={{ py: 4 }}>
          <Box
            sx={{
              alignItems: 'center',
              backgroundColor: 'background.default',
              display: 'flex',
              flexDirection: 'column',
              p: 3,
            }}>
            <ExclamationOutlinedIcon />
            <Typography color="textSecondary" sx={{ mt: 2 }} variant="body2">
              {error?.response?.data?.message}
            </Typography>
          </Box>
        </Box>
      );
    }

    return (
      <>
        <Box sx={{ py: 4 }}>
          <Box sx={{ mb: 2 }}>
            <Button
              color="primary"
              startIcon={<ArrowLeftIcon />}
              onClick={() => navigateBack('/hosts')}
              variant="text">
              {t('host:back')}
            </Button>
          </Box>
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              mb: 2,
            }}>
            <Typography color="textPrimary" variant="h4">
              {data?.firstName} {data?.lastName}
            </Typography>
          </Box>
          <Tabs
            allowScrollButtonsMobile
            value={activeTab}
            variant="scrollable"
            onChange={(e, newValue) => setActiveTab(newValue)}>
            {tabs.map(option => (
              <Tab key={option.label} label={option.label} />
            ))}
          </Tabs>
          <Divider sx={{ mb: 2 }} />
          <TabPanel value={activeTab} index={0}>
            <Grid
              container
              item
              lg={12}
              spacing={3}
              sx={{
                height: 'fit-content',
                order: {
                  md: 2,
                  xs: 1,
                },
              }}
              xs={12}>
              <Grid item xs={12}>
                <HostInfo host={data} />
              </Grid>
              <Grid item xs={12}>
                <DestinationGroupInfo
                  host={data}
                  handleOpenGroupDialog={handleOpenDestinationGroupDialog}
                />
              </Grid>
              <Grid item xs={12}>
                <DestinationInfo
                  handleOpenHostDestinationsDialog={handleOpenHostDestinationsDialog}
                  host={data}
                />
              </Grid>
            </Grid>
          </TabPanel>
          <TabPanel value={activeTab} index={1}>
            <Grid
              container
              item
              lg={12}
              spacing={3}
              sx={{
                height: 'fit-content',
                order: {
                  md: 2,
                  xs: 1,
                },
              }}
              xs={12}>
              <Grid item xs={12}>
                <BillingGroupInfo handleOpenGroupDialog={handleOpenGroupDialog} host={data} />
              </Grid>
              <Grid item xs={12}>
                <DiscountInfo
                  host={data}
                  discounts={discountsTemplatesData?.templates || []}
                  handleOpenUpdateDiscountDialog={handleOpenUpdateDiscountDialog}
                />
              </Grid>
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardHeader title={t('host:discount.history')} />
                  <Divider />
                  <HostsDiscountHistory
                    data={discountHistoryData?.resources || []}
                    error={discountHistoryError?.response?.data?.message || ''}
                    isLoading={isLoadingDiscountHistory}
                  />
                  <Divider sx={{ mt: 'auto' }} />
                  <Pagination
                    changePage={changePage}
                    changePerPage={changePerPage}
                    page={page}
                    isFetching={isFetchingDiscountHistory}
                    totalPages={totalPages}
                    perPage={perPage}
                  />
                </Card>
              </Grid>
            </Grid>
          </TabPanel>
        </Box>
        <Outlet />
      </>
    );
  };

  return (
    <Box
      sx={{
        backgroundColor: 'background.default',
        flexGrow: 1,
      }}>
      <Container
        maxWidth="lg"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}>
        {renderContent()}
      </Container>
      <UpdateHostDiscountsDialog
        open={isOpenUpdateDiscountDialog}
        hostId={hostId}
        discountTemplatesIds={data?.discountTemplates?.map(template => template.id) || []}
        billingGroupDiscountTemplatesIds={
          data?.billingGroup?.discountTemplates?.map(template => template.id) || []
        }
        onCancel={handleCloseUpdateDiscountDialog}
      />
      <UpdateHostDestinationsDialog
        open={isOpenUpdateHostDestinationsDialog}
        onCancel={handleCloseUpdateHostDestinationsDialog}
        destinationsIds={data?.destinations?.map(dest => dest.id) || []}
        hostId={hostId}
      />
      <UpdateHostBillingGroupDialog
        open={isOpenUpdateBillingGroupDialog}
        onCancel={handleCloseUpdateBillingGroupDialog}
        billingGroupId={data?.billingGroup?.id || 0}
        hostId={hostId}
      />
      <UpdateHostDestinationGroupDialog
        open={isOpenUpdateDestinationGroupDialog}
        onCancel={handleCloseUpdateDestinationGroupDialog}
        destinationGroupId={data?.destinationGroup?.id || 0}
        hostId={hostId}
      />
    </Box>
  );
};

export default withPermissions(permissions => permissions.cannot('read', 'Hosts'), Host);
